import { Link } from 'react-router-dom'

import type { CompetitionPerformance, PacingStatus } from '@/api/types'
import {
  Card,
  DataTable,
  EmptyState,
  formatCurrency,
  formatInteger,
  formatPercent,
  Icon,
  PacingBadge,
  type Column,
  type FormatOptions,
} from '@/design'
import { formatDateKey } from '@/lib/filters'

import { CardQueryBody } from './CardQueryBody'
import { useDrillDown, useMoney, useOverviewQuery } from './hooks'

const TOP_COMPETITIONS = 8
const PACING_ORDER: Record<PacingStatus, number> = { behind: 0, on_track: 1, ahead: 2 }

/** A figure the source cannot give: a dash, never a 0. */
const NOT_AVAILABLE = <span className="ov-muted">—</span>

/** A missing figure sorts as the lowest value: apart from the real ones, never mixed in among them as a 0. */
function sortable(value: number | null): number {
  return value ?? Number.NEGATIVE_INFINITY
}

/** "Cars · closes 30 Sep", leaving out whichever part is unknown. */
function detail(row: CompetitionPerformance): string {
  return [row.category, row.closesOn ? `closes ${formatDateKey(row.closesOn)}` : null].filter(Boolean).join(' · ')
}

/** A row's figure that a source may not serve at all. */
type Figure = (row: CompetitionPerformance) => unknown

/** A column, with the figure it shows when that figure is one a source may not serve. */
type CompetitionColumn = Column<CompetitionPerformance> & { figure?: Figure }

/** Whether any row has the figure: a column no row fills (conversion, velocity and pacing on the live API) is left out, not a column of dashes. */
function hasFigure(figure: Figure, rows: readonly CompetitionPerformance[]): boolean {
  return rows.length === 0 || rows.some((row) => figure(row) !== null)
}

function columns(money: FormatOptions): CompetitionColumn[] {
  return [
    {
      key: 'name',
      header: 'Competition',
      sortValue: (row) => row.entity.label,
      cell: (row) => (
        <span className="ov-cell-stack">
          <span>{row.entity.label}</span>
          {detail(row) ? <span className="ov-cell-sub">{detail(row)}</span> : null}
        </span>
      ),
    },
    { key: 'revenue', header: 'Revenue', align: 'end', sortValue: (row) => row.revenue, cell: (row) => formatCurrency(row.revenue, money) },
    { key: 'tickets', header: 'Entries sold', align: 'end', sortValue: (row) => row.ticketsSold, cell: (row) => formatInteger(row.ticketsSold) },
    {
      key: 'conversion',
      header: 'Conversion',
      align: 'end',
      figure: (row) => row.conversionRate,
      sortValue: (row) => sortable(row.conversionRate),
      cell: (row) => (row.conversionRate === null ? NOT_AVAILABLE : formatPercent(row.conversionRate)),
    },
    {
      key: 'sellThrough',
      header: 'Sell-through',
      align: 'end',
      figure: (row) => row.sellThrough,
      sortValue: (row) => sortable(row.sellThrough),
      cell: (row) =>
        row.sellThrough === null ? (
          NOT_AVAILABLE
        ) : (
          <span className="ov-sell">
            <span className="ov-sell__track" aria-hidden="true">
              <span className="ov-sell__bar" style={{ width: `${Math.min(100, row.sellThrough * 100)}%` }} />
            </span>
            {formatPercent(row.sellThrough, 0)}
          </span>
        ),
    },
    {
      key: 'velocity',
      header: 'Velocity / day',
      align: 'end',
      figure: (row) => row.velocityPerDay,
      sortValue: (row) => sortable(row.velocityPerDay),
      cell: (row) => (row.velocityPerDay === null ? NOT_AVAILABLE : formatInteger(row.velocityPerDay)),
    },
    {
      key: 'pacing',
      header: 'Pacing',
      figure: (row) => row.pacing,
      sortValue: (row) => (row.pacing === null ? Number.NEGATIVE_INFINITY : PACING_ORDER[row.pacing]),
      cell: (row) => (row.pacing === null ? NOT_AVAILABLE : <PacingBadge status={row.pacing} />),
    },
  ]
}

/** The competitions earning most in the period, with sell-through and, where the source serves it, how each is pacing towards its close. */
export function TopCompetitionsCard() {
  const competitions = useOverviewQuery('top-competitions', (client, filters) => client.topCompetitions(filters, TOP_COMPETITIONS))
  const href = useDrillDown()
  const money = useMoney(competitions.data?.meta.currency)
  const pacing = hasFigure((row) => row.pacing, competitions.data?.data ?? [])

  return (
    <Card
      title="Top competitions"
      subtitle={`By revenue in the period · sell-through${pacing ? ' and pacing' : ''} to date`}
      busy={competitions.isFetching}
      footer={
        <Link className="ov-footer-link" to={href({ path: '/analytics/competitions' })}>
          All competitions <Icon name="arrowRight" size={14} />
        </Link>
      }
    >
      <CardQueryBody query={competitions} loadingRows={6} errorDescription="Competitions could not be loaded.">
        {({ data }) => (
          <DataTable
            caption="Top competitions"
            columns={columns(money).filter((column) => !column.figure || hasFigure(column.figure, data))}
            rows={data}
            rowKey={(row) => row.entity.id}
            rowHref={(row) => href(row.drillDown)}
            initialSort={{ key: 'revenue', direction: 'desc' }}
            empty={<EmptyState variant="no-results" compact title="No competition sales in this period" />}
          />
        )}
      </CardQueryBody>
    </Card>
  )
}
